/**
 * Game state hook — subscribes to game events and maintains local game state.
 * Handles Tic-Tac-Toe move validation and state transitions.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useNostrPublisher, useNostrSubscription } from "./useNostr";
import { useNostrUser } from "./useNostr";
import {
  KIND_GAME_MOVE,
  KIND_GAME_STATE,
  KIND_MATCH_RESULT,
  GAME_TICTACTOE,
  TAG_GAME_ID,
  TAG_GAME_TYPE,
  TAG_MOVE,
  TAG_TURN,
  TAG_WINNER,
  TAG_TEAM_A,
  TAG_TEAM_B,
} from "@/lib/gameKinds";
import {
  createGame,
  applyMove,
  encodeBoard,
  decodeBoard,
  type TicTacToeState,
  type Player,
  type Board,
  WINNING_LINES,
  getWinningLine,
} from "@/lib/tictactoe";
import type { NostrEvent } from "@nostrify/types";

export interface GameInfo {
  gameId: string;
  gameType: string;
  teamA: string;
  teamAName: string;
  teamB: string;
  teamBName: string;
}

export interface GameState {
  gameInfo: GameInfo;
  ticTacToe: TicTacToeState;
  myTeam: "a" | "b";
  isMyTurn: boolean;
  moveHistory: Array<{ player: Player; cell: number; timestamp: number }>;
  error: string | null;
}

export function useGame(gameInfo: GameInfo | null, teamId: string | null) {
  const { user } = useNostrUser();
  const { publish } = useNostrPublisher();
  const { subscribe } = useNostrSubscription();

  const [gameState, setGameState] = useState<TicTacToeState>(createGame());
  const [moveHistory, setMoveHistory] = useState<Array<{ player: Player; cell: number; timestamp: number }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [isMyTurn, setIsMyTurn] = useState(false);

  const stateRef = useRef(gameState);
  stateRef.current = gameState;

  // Determine which side we are
  let myTeam: "a" | "b" | null = null;
  if (gameInfo && teamId) {
    if (gameInfo.teamA === teamId) {
      myTeam = "a";
    } else if (gameInfo.teamB === teamId) {
      myTeam = "b";
    }
  }

  // Determine whose turn within the team (round-robin among members)
  // For simplicity, any team member can make a move when it's their team's turn
  useEffect(() => {
    if (myTeam) {
      setIsMyTurn(gameState.turn === myTeam && !gameState.winner);
    }
  }, [gameState.turn, gameState.winner, myTeam]);

  // Subscribe to game moves
  useEffect(() => {
    if (!gameInfo) return;

    const cleanup = subscribe(
      [
        {
          kinds: [KIND_GAME_MOVE],
          "#i": [gameInfo.gameId],
          since: Math.floor(Date.now() / 1000) - 3600,
        },
        {
          kinds: [KIND_GAME_STATE],
          "#i": [gameInfo.gameId],
          limit: 1,
        },
      ],
      (event: NostrEvent) => {
        if (event.kind === KIND_GAME_STATE) {
          // Load existing game state
          try {
            const content = JSON.parse(event.content);
            if (content.board) {
              const board = decodeBoard(content.board);
              const turn: Player = content.turn ?? "a";
              const winner = content.winner ?? null;
              setGameState({
                board,
                turn,
                winner,
                moveCount: board.filter((c) => c !== null).length,
              });
            }
          } catch {
            // Ignore parse errors
          }
          return;
        }

        if (event.kind === KIND_GAME_MOVE) {
          const moveTag = event.tags.find((t) => t[0] === TAG_MOVE)?.[1];
          const turnTag = event.tags.find((t) => t[0] === TAG_TURN)?.[1];
          if (!moveTag || !turnTag) return;

          const cell = parseInt(moveTag, 10);
          const turn = turnTag as Player;

          setGameState((prev) => {
            const result = applyMove(prev, cell, turn);
            if (result) {
              setMoveHistory((h) => [...h, { player: turn, cell, timestamp: event.created_at }]);
              return result;
            }
            return prev;
          });

          // Check for game end and publish result
          setGameState((prev) => {
            if (prev.winner && prev.winner !== "draw") {
              // Publish match result
              publishMatchResult(gameInfo, prev, event.tags).catch(console.error);
            } else if (prev.winner === "draw") {
              publishMatchResult(gameInfo, prev, event.tags).catch(console.error);
            }
            return prev;
          });
        }
      },
    );

    return cleanup;
  }, [gameInfo?.gameId, subscribe]);

  // Make a move
  const makeMove = useCallback(async (cell: number) => {
    if (!user || !gameInfo || !myTeam) return;
    if (!isMyTurn) {
      setError("It's not your team's turn");
      return;
    }

    const result = applyMove(stateRef.current, cell, myTeam);
    if (!result) {
      setError("Invalid move");
      return;
    }

    // Apply locally immediately for responsiveness
    setGameState(result);
    setMoveHistory((h) => [...h, { player: myTeam, cell, timestamp: Math.floor(Date.now() / 1000) }]);
    setError(null);

    // Publish move to Nostr
    const moveEvent = {
      kind: KIND_GAME_MOVE,
      content: JSON.stringify({
        board: encodeBoard(result.board),
        cell,
        player: myTeam,
        turn: result.turn,
      }),
      tags: [
        [TAG_GAME_ID, gameInfo.gameId],
        [TAG_GAME_TYPE, gameInfo.gameType],
        [TAG_TEAM_A, gameInfo.teamA],
        [TAG_TEAM_B, gameInfo.teamB],
        [TAG_MOVE, String(cell)],
        [TAG_TURN, myTeam],
        ["prev_turn", result.turn],
        ...(result.winner ? [[TAG_WINNER, result.winner]] : []),
      ],
    };

    await publish(moveEvent);

    // If game is over, publish final state
    if (result.winner) {
      await publishMatchResult(gameInfo, result, moveEvent.tags);
    }
  }, [user, gameInfo, myTeam, isMyTurn, publish]);

  const publishMatchResult = async (
    info: GameInfo,
    state: TicTacToeState,
    tags: string[][],
  ) => {
    const resultEvent = {
      kind: KIND_MATCH_RESULT,
      content: JSON.stringify({
        board: encodeBoard(state.board),
        winner: state.winner,
        moveCount: state.moveCount,
      }),
      tags: [
        [TAG_GAME_ID, info.gameId],
        [TAG_GAME_TYPE, info.gameType],
        [TAG_TEAM_A, info.teamA],
        [TAG_TEAM_B, info.teamB],
        [TAG_WINNER, state.winner ?? "draw"],
      ],
    };
    await publish(resultEvent);
  };

  const winningLine = getWinningLine(gameState.board);

  const fullState: GameState | null = gameInfo && myTeam
    ? {
        gameInfo,
        ticTacToe: gameState,
        myTeam,
        isMyTurn,
        moveHistory,
        error,
      }
    : null;

  return {
    gameState: fullState,
    makeMove,
    winningLine,
    error,
  };
}